<script setup>
import { ref, onBeforeUpdate } from 'vue'
import BingoSquare from './BingoSquare.ce.vue'
import { ROUND_STATUS } from '../constants.js'

const props = defineProps({
  gridSquares: { type: Array, required: true },
  gridCols: { type: Number, default: 3 },
  canMark: { type: Boolean, default: false },
  roundStatus: { type: String, default: null },
})

defineEmits(['toggle-square'])

const squareRefs = ref([])
const fitted = ref(false)
onBeforeUpdate(() => {
  squareRefs.value = []
})

let refitTimer = null
function scheduleRefit() {
  clearTimeout(refitTimer)
  refitTimer = setTimeout(() => {
    refitTimer = null
    const sizes = squareRefs.value
      .map((sq) => sq?.measureBestSize?.())
      .filter((s) => s != null)
    if (sizes.length > 0) {
      const min = Math.min(...sizes)
      squareRefs.value.forEach((sq) => sq?.applySize?.(min))
    }
    fitted.value = true
  }, 120)
}
</script>

<template>
  <div class="bingo-card-wrapper">
    <div
      class="bingo-card"
      :class="[`bingo-card--${roundStatus}`, { 'bingo-card--fitting': !fitted }]"
      :style="{ gridTemplateColumns: `repeat(${props.gridCols}, 1fr)` }"
    >
      <template v-for="(row, rowIndex) in gridSquares" :key="rowIndex">
        <BingoSquare
          v-for="(cardSquare, colIndex) in row"
          :key="colIndex"
          :ref="(el) => el && squareRefs.push(el)"
          :cardSquare="cardSquare"
          :canMark="canMark"
          @toggle="$emit('toggleSquare', cardSquare)"
          @needs-refit="scheduleRefit"
        />
      </template>
    </div>
    <div v-if="roundStatus === ROUND_STATUS.PREPARING" class="status-overlay">
      <p class="status-text">Wachten tot het spel begint...</p>
    </div>
  </div>
</template>

<style scoped>
.bingo-card-wrapper {
  position: relative;
  width: 100%;
  max-width: 600px;
}

.bingo-card {
  display: grid;
  gap: 8px;
  padding: 8px;
  background: var(--bingo-card-bg, rgba(0, 0, 0, 0.2));
  border-radius: 12px;
  transition: filter 0.3s ease;
}

.bingo-card--preparing {
  filter: grayscale(0.6) brightness(0.7);
}

.bingo-card--fitting {
  visibility: hidden;
}

.status-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.status-text {
  font-family: var(--bingo-font-heading);
  text-transform: uppercase;
  font-size: 1.3rem;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  padding: 12px 24px;
  border-radius: 8px;
}
</style>
